<template>
  <adn-message
    v-if="shouldDisplayMessage"
    :title="i18n._('Mentions réglementaires')"
    kind="warning"
    class="block-regulatory"
  >
    <p>{{ messageContent }}</p>
  </adn-message>
</template>

<script lang="ts">
import { type PropType, computed, defineComponent } from 'vue'
import { formatDate } from '@b2ag/l10n'
import type { Product } from '../typings'
import AdnMessage from '@b2ag/design-system/src/components/AdnMessage/AdnMessage.vue'
import { i18n } from '@b2ag/locale'

export default defineComponent({
  name: 'PProductAlertRegulatory',
  components: {
    AdnMessage,
  },
  props: {
    product: {
      type: Object as PropType<Product>,
      default: () => ({}),
    },
  },
  setup(props) {
    const { product } = props

    const isDatePassed = (date?: string) => {
      if (!date) return false
      return new Date(date) < new Date()
    }

    const useEndDatePassed = computed(() => isDatePassed(product.useEndDatePm))
    const marketingEndDatePassed = computed(() => isDatePassed(product.marketingEndDatePm))

    const formattedUseEndDate = computed(() =>
      product.useEndDatePm ? formatDate(new Date(product.useEndDatePm), 'dd MMMM yyyy') : '',
    )

    const shouldDisplayMessage = computed(() => {
      // Case 1: No display if both dates are missing or not passed
      if (
        (!product.useEndDatePm || !useEndDatePassed.value) &&
        (!product.marketingEndDatePm || !marketingEndDatePassed.value)
      ) {
        return false
      }

      // Case 4 & 5: No display if use end date is passed
      if (useEndDatePassed.value) {
        return false
      }

      // Display for cases 2 & 3
      return true
    })

    const messageContent = computed(() => {
      // Case 2: Marketing end date passed, no use end date
      if (!product.useEndDatePm && marketingEndDatePassed.value) {
        return `${i18n._('Attention : dans le cadre de son arrêt réglementaire, le site Aladin ainsi que votre distributeur vous informent que ce produit ne peut plus être commercialisé')}.`
      }

      // Case 3: Use end date not passed but marketing end date passed
      if (product.useEndDatePm && !useEndDatePassed.value && marketingEndDatePassed.value) {
        return `${i18n._('Attention : dans le cadre de son arrêt réglementaire, le site Aladin ainsi que votre distributeur vous informent que ce produit ne doit plus être utilisé à partir du {formattedUseEndDate}', { formattedUseEndDate: formattedUseEndDate.value })}.`
      }

      return ''
    })

    return {
      shouldDisplayMessage,
      messageContent,
      i18n,
    }
  },
})
</script>

<style lang="scss">
.block-regulatory {
  margin: var(--spacing-5) 0 var(--spacing-5) 0;

  @media (max-width: $breakpoint-md) {
    margin: var(--spacing-3) var(--spacing-3) var(--spacing-3) var(--spacing-3);
  }

  padding: var(--spacing-3) var(--spacing-3) var(--spacing-3);
  border-color: var(--color-warning);
  border-width: 1px 1px 1px 4px;
  border-style: solid;
  border-radius: var(--border-radius-2);

  .adn-alert__text {
    color: var(--color-warning-dark);
  }

  .adn-message-text {
    color: var(--color-warning-dark);
    font-size: var(--font-size-m);
  }
}

.adn-message {
  background-color: var(--color-warning-background) !important;
}
</style>
